import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import preloaded from '~/preloaded';
import { fetchPatrolsAccounts, fetchPatrolsAccountState } from '~/api/patrols';
import { arrayToObjectByKey } from '~/utils';

import { getRewardsObjectByBundlesList } from '~/pages/Patrols/helpers';

import type { PayloadAction } from '@reduxjs/toolkit';
import type { Currencies } from '@wg/wows-entities/const';
import type { AppAsyncThunk } from '~/store';
import type {
  IApiGetAccountStateResponse,
  IApiGetMembersResponse,
  IPatrol,
  IPatrolBundle,
  IPatrolBundleId,
  IPatrolId,
  IPatrolReward,
  IPatrolRewardId,
} from '~/types/patrols';

interface IPatrolsState {
  eventStartsAt: Nullable<number>;
  eventFinishesAt: Nullable<number>;
  nextUpdateAt: Nullable<number>;
  patrols: IPatrol[];
  bonusType: Nullable<Currencies>;
  superBundleChanceStep: number;
  bundles: Record<IPatrolBundleId, IPatrolBundle>;
  rewards: Record<IPatrolRewardId, IPatrolReward>;
  members: IApiGetMembersResponse;
  account: IApiGetAccountStateResponse;
}

const patrolsSettings = preloaded?.settings.patrols;

const initialState: IPatrolsState = {
  eventStartsAt: patrolsSettings?.eventStartsAt || null,
  eventFinishesAt: patrolsSettings?.eventFinishesAt || null,
  nextUpdateAt: patrolsSettings?.nextUpdateAt || null,
  patrols: patrolsSettings?.patrols || null,
  bonusType: patrolsSettings?.bonusType || null,
  superBundleChanceStep: patrolsSettings?.superBundleChanceStep || 0,
  bundles: patrolsSettings?.bundles ? arrayToObjectByKey(patrolsSettings.bundles, 'id') : {},
  rewards: patrolsSettings?.bundles ? getRewardsObjectByBundlesList(patrolsSettings.bundles) : {},
  members: {
    timestamp: 0,
    result: {},
  },
  account: {
    timestamp: 0,
    result: null,
  },
};

const patrolsSlice = createSlice({
  name: 'patrols',
  initialState,
  reducers: {
    setMembers: (state: IPatrolsState, action: PayloadAction<IApiGetMembersResponse>) => {
      if (action.payload.timestamp < state.members.timestamp) {
        return;
      }
      state.members = action.payload;
    },

    setAccount: (state: IPatrolsState, action: PayloadAction<IApiGetAccountStateResponse>) => {
      if (action.payload.timestamp < state.members.timestamp) {
        return;
      }
      state.account = action.payload;
    },
  },
});

const patrolsActions = patrolsSlice.actions;

export default patrolsSlice.reducer;

export const getPatrolsAccountStateThunk =
  (signal?: AbortSignal): AppAsyncThunk =>
  async (dispatch) => {
    try {
      const response = await fetchPatrolsAccountState(signal);

      if (response?.timestamp) {
        dispatch(patrolsActions.setAccount(response));
      }
    } catch (error: unknown) {
      if (!axios.isCancel(error)) {
        console.error('Failed get patrols data', error);
      }
    }
  };

export const getPatrolsAccountsStateThunk =
  (patrols: IPatrolId[], signal?: AbortSignal): AppAsyncThunk =>
  async (dispatch) => {
    try {
      const response = await fetchPatrolsAccounts(patrols, signal);

      if (response?.timestamp) {
        dispatch(patrolsActions.setMembers(response));
      }
    } catch (error: unknown) {
      if (!axios.isCancel(error)) {
        console.error('Failed get patrols data', error);
      }
    }
  };
